import type { ProxyCallResult } from "../types";

// TODO: Migrate remaining API functions to use safeJsonParse for consistent error logging
// Functions already migrated: updateCustomerDog, updateSubscriptionMeals

/**
 * Safely parse JSON from a Response, logging any parse errors
 * Returns null if parsing fails
 */
async function safeJsonParse(res: Response, context: string): Promise<any> {
  try {
    return await res.json();
  } catch (err) {
    console.warn(`[${context}] Failed to parse JSON response:`, err);
    return null;
  }
}

export async function callProxy<TBody = any>(
  path: string
): Promise<ProxyCallResult<TBody>> {
  const t0 = performance.now();

  try {
    const res = await fetch(path, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    const ms = Math.round(performance.now() - t0);

    const text = await res.text();
    let body: any = null;

    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = { raw: text };
    }

    return {
      ok: res.ok,
      status: res.status,
      path,
      ms,
      body,
      error: !res.ok
        ? body?.error || `Request failed (${res.status})`
        : undefined,
    };
  } catch (e: any) {
    const ms = Math.round(performance.now() - t0);
    return {
      ok: false,
      status: 0,
      path,
      ms,
      body: null,
      error: e?.message || "Network error",
    };
  }
}

function qp(shop: string) {
  return `shop=${encodeURIComponent(shop)}`;
}

export function buildPath(proxyBase: string, route: string, shop: string) {
  const base = proxyBase || "/apps/paws";
  const r = route.startsWith("/") ? route : `/${route}`;
  return `${base}${r}?${qp(shop)}`;
}

export async function fetchBootstrap<TBootstrap = any>(
  proxyBase: string,
  shop: string
) {
  return callProxy<TBootstrap>(buildPath(proxyBase, "/bootstrap", shop));
}

export async function fetchPaymentUpdateUrl(
  proxyBase: string,
  shop: string
): Promise<ProxyCallResult<{ url?: string }>> {
  return callProxy(
    buildPath(proxyBase, "/recharge/payment-method/update-url", shop)
  );
}

export async function updateCustomerDog(proxyBase: string, shop: string, body: any) {
  const url = `${proxyBase}/account/dogs/update?shop=${encodeURIComponent(shop)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  const json = await safeJsonParse(res, "updateCustomerDog");

  if (!res.ok) {
    const errorMsg = json?.error || json?.message || "Failed to update dog profile";
    console.error("[updateCustomerDog] API error:", errorMsg);
  }

  return { ok: res.ok, body: json, error: json?.error };
}

export async function updateSubscriptionMeals(
  proxyBase: string,
  shop: string,
  subscriptionId: string,
  lineItems: { variantId: string; quantity: number; productTitle?: string; variantTitle?: string; price?: number }[],
  newBundleVariantId?: string | null,
  customerId?: string
) {
  // Build URL with shop parameter, and optionally customer_id for development
  let url = `${proxyBase}/recharge/subscription/update-meals?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      subscriptionId,
      lineItems,
      newBundleVariantId, // Optional: Change the plan bundle
    }),
  });

  const json = await safeJsonParse(res, "updateSubscriptionMeals");

  if (!res.ok) {
    throw new Error(json?.error || json?.message || "Failed to update subscription");
  }

  return { ok: res.ok, body: json };
}

export async function skipNextDelivery(
  _proxyBase: string,
  shop: string,
  subscriptionId: string,
  _customerId?: string,
  rechargeAddressId?: string | number
): Promise<{ ok: boolean; body: any }> {
  const res = await fetch(`${QIVORA_BASE}/public/subscription-skip`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      shop,
      subscription_id: subscriptionId,
      recharge_address_id: rechargeAddressId ? Number(rechargeAddressId) : undefined,
    }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    throw new Error(json?.error?.message || json?.error || json?.message || "Failed to skip delivery");
  }

  return { ok: res.ok, body: json };
}

export async function sendPaymentUpdateEmail(
  proxyBase: string,
  shop: string,
  customerId?: string,
  paymentMethodId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/payment-method/send-update-email?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ paymentMethodId }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  return {
    ok: res.ok,
    body: json,
    error: !res.ok ? json?.error || json?.message || "Failed to send email" : undefined,
  };
}

export async function deletePaymentMethod(
  proxyBase: string,
  shop: string,
  paymentMethodId: string,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/payment-method/delete?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ paymentMethodId }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  return {
    ok: res.ok,
    body: json,
    error: !res.ok ? json?.error || json?.message || "Failed to delete payment method" : undefined,
  };
}

// -----------------------------
// Soft Delete / Restore Dog
// -----------------------------

export async function softDeleteDog(
  proxyBase: string,
  shop: string,
  body: { dogId: string }
): Promise<{ ok: boolean; body: any; error?: string }> {
  const url = `${proxyBase}/account/dogs/soft-delete?shop=${encodeURIComponent(shop)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  const json = await safeJsonParse(res, "softDeleteDog");

  return {
    ok: res.ok,
    body: json,
    error: !res.ok ? json?.error || json?.message || "Failed to remove dog" : undefined,
  };
}

export async function restoreDog(
  proxyBase: string,
  shop: string,
  body: { dogId: string }
): Promise<{ ok: boolean; body: any; error?: string }> {
  const url = `${proxyBase}/account/dogs/restore?shop=${encodeURIComponent(shop)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });

  const json = await safeJsonParse(res, "restoreDog");

  return {
    ok: res.ok,
    body: json,
    error: !res.ok ? json?.error || json?.message || "Failed to restore dog" : undefined,
  };
}

// -----------------------------
// Delivery Date Management
// -----------------------------

export type AvailableDeliveryDate = {
  date: string;
  dayOfWeek?: string;
  shipDate: string;
};

const QIVORA_BASE = "https://custom-qivora.northcode.uk";

/**
 * Fetch available delivery dates from Qivora public API.
 * Returns cutoff_hours so the frontend can determine when changes are no longer allowed.
 */
export async function fetchAvailableDeliveryDates(
  _proxyBase: string,
  shop: string,
  weeksAhead: number = 8,
  _customerId?: string
): Promise<{ ok: boolean; availableDates: AvailableDeliveryDate[]; blockedDates: string[]; cutoffHours: number; timezone: string; deliveryDayNames: string[]; error?: string }> {
  try {
    const qivoraRes = await fetch(
      `${QIVORA_BASE}/public/available-dates?shop=${encodeURIComponent(shop)}&weeks=${weeksAhead}`
    );
    if (!qivoraRes.ok) {
      return {
        ok: false,
        availableDates: [],
        blockedDates: [],
        cutoffHours: 36,
        timezone: "Europe/London",
        deliveryDayNames: [],
        error: "Failed to fetch available dates",
      };
    }

    const data = await qivoraRes.json();
    const dates: AvailableDeliveryDate[] = ((data?.dates ?? []) as Array<{ date: string; ship_date: string }>).map((d) => {
      const dateObj = new Date(d.date + "T00:00:00");
      const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
      return {
        date: d.date,
        dayOfWeek: dayNames[dateObj.getDay()],
        shipDate: d.ship_date,
      };
    });

    return {
      ok: true,
      availableDates: dates,
      blockedDates: [],
      cutoffHours: data.cutoff_hours ?? 36,
      timezone: data.timezone ?? "Europe/London",
      deliveryDayNames: (data.allowed_delivery_names ?? []) as string[],
    };
  } catch {
    return {
      ok: false,
      availableDates: [],
      blockedDates: [],
      cutoffHours: 36,
      timezone: "Europe/London",
      deliveryDayNames: [],
      error: "Failed to connect to delivery scheduling service",
    };
  }
}

export async function changeDeliveryDate(
  _proxyBase: string,
  shop: string,
  subscriptionId: string,
  deliveryDate: string,
  _customerId?: string,
  rechargeAddressId?: string | number
): Promise<{ ok: boolean; body: any; error?: string; errors?: string[] }> {
  const res = await fetch(`${QIVORA_BASE}/public/subscription-reschedule`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      shop,
      subscription_id: subscriptionId,
      recharge_address_id: rechargeAddressId ? Number(rechargeAddressId) : undefined,
      delivery_date: deliveryDate,
    }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    const errorMsg = json?.error?.message || json?.error || json?.message || "Failed to change delivery date";
    return {
      ok: false,
      body: json,
      error: errorMsg,
      errors: json?.errors,
    };
  }

  return {
    ok: true,
    body: json,
  };
}

// -----------------------------
// Daily Serving / Pack Size
// -----------------------------

export async function changeDailyServing(
  proxyBase: string,
  shop: string,
  subscriptionId: string,
  newDailyGrams: number,
  newPackSizeGrams: number,
  newVariantId: string,
  meals: Array<{ variantId: string; count: number; productTitle?: string; variantTitle?: string; price?: number }>,
  newPrice: number,
  mealsSummary: string,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  // Build URL with shop parameter, and optionally customer_id for development
  let url = `${proxyBase}/recharge/subscription/change-daily-serving?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      subscriptionId,
      newDailyGrams,
      newPackSizeGrams,
      newVariantId,
      meals,
      newPrice,
      mealsSummary,
    }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to change daily serving",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

// -----------------------------
// Change Delivery Frequency
// -----------------------------

export async function changeDeliveryFrequency(
  proxyBase: string,
  shop: string,
  subscriptionId: string,
  newPlanDays: number,
  newPackSizeGrams: number,
  newVariantId: string,
  meals: Array<{ variantId: string; count: number; productTitle?: string; variantTitle?: string; price?: number }>,
  newPrice: number,
  mealsSummary: string,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  // Build URL with shop parameter, and optionally customer_id for development
  let url = `${proxyBase}/recharge/subscription/change-frequency?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      subscriptionId,
      newPlanDays,
      newPackSizeGrams,
      newVariantId,
      meals,
      newPrice,
      mealsSummary,
    }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to change delivery frequency",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

// -----------------------------
// Change Quantity (Legacy Subscriptions)
// -----------------------------

export async function changeQuantity(
  proxyBase: string,
  shop: string,
  subscriptionId: string,
  quantity: number,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/subscription/update-quantity?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      subscriptionId,
      quantity,
    }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to change quantity",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

// -----------------------------
// Add Treats to Customer
// -----------------------------

export async function addTreatsToCustomer(
  proxyBase: string,
  shop: string,
  treats: Array<{ variantId: string; quantity: number }>,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/subscription/add-treats?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ treats }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to add treats",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

// -----------------------------
// Cancel Subscription
// -----------------------------

export async function cancelSubscription(
  proxyBase: string,
  shop: string,
  subscriptionId: string,
  cancellationReason: string,
  cancellationFeedback: string,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/subscription/cancel?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      subscriptionId,
      cancellationReason,
      cancellationFeedback,
    }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to cancel subscription",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

// -----------------------------
// Resume (reactivate) Subscription
// -----------------------------

export async function resumeSubscription(
  proxyBase: string,
  shop: string,
  subscriptionId: string,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/subscription/resume?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ subscriptionId }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok || json?.error) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to resume subscription",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

// -----------------------------
// Address Management
// -----------------------------

export type AddressData = {
  first_name?: string;
  last_name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
  phone?: string;
};

export async function updateAddressFields(
  proxyBase: string,
  shop: string,
  addressId: string,
  addressData: AddressData,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/address/update-fields?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ addressId, addressData }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to update address",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

export async function moveSubscriptionToAddress(
  proxyBase: string,
  shop: string,
  subscriptionId: string,
  newAddressId: string,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/address/move-subscription?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ subscriptionId, newAddressId }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to move subscription",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

export async function mergeAddresses(
  proxyBase: string,
  shop: string,
  sourceAddressId: string,
  targetAddressId: string,
  deleteSource: boolean = true,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string; movedCount?: number }> {
  let url = `${proxyBase}/recharge/address/merge?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ sourceAddressId, targetAddressId, deleteSource }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to merge addresses",
    };
  }

  return {
    ok: true,
    body: json,
    movedCount: json?.movedCount,
  };
}

// -----------------------------
// One-time Products (Add to Next Order)
// -----------------------------

export async function addOnetimeToNextOrder(
  proxyBase: string,
  shop: string,
  addressId: string,
  shopifyVariantId: string,
  quantity: number,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/onetimes/add?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ addressId, shopifyVariantId, quantity }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to add product to next order",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

export async function getOnetimesForAddress(
  proxyBase: string,
  shop: string,
  addressId: string,
  customerId?: string
): Promise<{ ok: boolean; body: any; onetimes: any[]; error?: string }> {
  let url = `${proxyBase}/recharge/onetimes/list?shop=${encodeURIComponent(shop)}&address_id=${encodeURIComponent(addressId)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      onetimes: [],
      error: json?.error || json?.message || "Failed to fetch one-time products",
    };
  }

  return {
    ok: true,
    body: json,
    onetimes: json?.onetimes || [],
  };
}

export async function deleteOnetime(
  proxyBase: string,
  shop: string,
  onetimeId: string,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/onetimes/delete?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ onetimeId }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || json?.message || "Failed to remove product",
    };
  }

  return {
    ok: true,
    body: json,
  };
}

export async function updateOnetimeQuantity(
  proxyBase: string,
  shop: string,
  onetimeId: string,
  quantity: number,
  customerId?: string
): Promise<{ ok: boolean; body: any; error?: string }> {
  let url = `${proxyBase}/recharge/onetimes/update?shop=${encodeURIComponent(shop)}`;
  if (customerId) {
    url += `&customer_id=${encodeURIComponent(customerId)}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ onetimeId, quantity }),
  });

  let json: any = null;
  try {
    json = await res.json();
  } catch {}

  if (!res.ok) {
    return {
      ok: false,
      body: json,
      error: json?.error || "Failed to update quantity",
    };
  }

  return { ok: true, body: json };
}